import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import {
  FiUsers, FiInbox, FiHash, FiSearch, FiX, FiUserCheck, FiUserX,
  FiChevronLeft, FiChevronRight, FiMail, FiPhone, FiFileText, FiArrowRight, FiGlobe,
} from 'react-icons/fi';
import BASE_URL, { IMAGE_DOMAIN } from '../../config/config';
import { claves, cabecerasAuth } from '../../queries/cliente';
import { formatearFechaLimite } from '../../utils/fechaLimite';
import Loader from '../../components/Loader';
import Pestanas from '../../components/ui/Pestanas';
import DtDropdown from './dashboard/DtDropdown';

const DEPARTAMENTOS = ['no_especificado', 'diseno', 'marketing', 'desarrollo', 'redes_sociales'];
const ESTADOS = ['abierto', 'cerrado', 'retenido'];
const TICKETS_POR_PAGINA = 10;
const PERSONAS_POR_PAGINA = 12;

/**
 * Tickets de las cuentas a cargo del usuario.
 *
 * Solo existe para quien supervisa a alguien: el propio endpoint devuelve la
 * jerarquía de quien pregunta, así que nunca se puede pedir el equipo de otro.
 *
 * Dos pestañas porque una plantilla grande no cabe en fichas: con 60 empleados,
 * una tarjeta por cabeza llena la pantalla antes de llegar a los tickets. En
 * "Tickets" la persona es un filtro más de la barra (el desplegable trae su
 * propio buscador) y en "Personas" está el listado completo, ordenado por quién
 * acumula más abiertos, que es lo que un responsable mira primero.
 *
 * La tabla es la de "Mis tickets" (clases `dt-*`) con una columna añadida para
 * el solicitante, para que un ticket se lea igual en las dos pantallas.
 */
const MiEquipo = () => {
  const { t } = useTranslation();
  const token = localStorage.getItem('AuthToken');
  const tokenInfo = token ? jwtDecode(token) : {};

  const [pestana, setPestana] = useState('tickets');
  const [solicitante, setSolicitante] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [dpto, setDpto] = useState('');
  const [estado, setEstado] = useState('');
  const [pagina, setPagina] = useState(1);

  const [busquedaPersona, setBusquedaPersona] = useState('');
  const [paginaPersonas, setPaginaPersonas] = useState(1);

  const consulta = useQuery({
    queryKey: claves.miEquipo(tokenInfo.usuario),
    queryFn: async () => (await axios.get(`${BASE_URL}/mi-equipo`, cabecerasAuth())).data,
    enabled: Boolean(tokenInfo.usuario),
  });

  const equipo = useMemo(() => consulta.data?.equipo || [], [consulta.data]);
  const tickets = useMemo(
    () => [...(consulta.data?.tickets || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [consulta.data],
  );

  const nombreDe = (persona) => [persona.nombre, persona.apellidos].filter(Boolean).join(' ') || persona.usuario;

  // Nombre por usuario, para la columna "Solicitante" sin buscar en cada fila
  const nombrePorUsuario = useMemo(
    () => Object.fromEntries(equipo.map((p) => [p.usuario, [p.nombre, p.apellidos].filter(Boolean).join(' ') || p.usuario])),
    [equipo],
  );

  const totalAbiertos = equipo.reduce((acc, p) => acc + p.tickets_abiertos, 0);

  // ── Pestaña de tickets ────────────────────────────────────────────────
  const hayFiltros = busqueda !== '' || dpto !== '' || estado !== '' || solicitante !== '';

  const limpiarFiltros = () => {
    setBusqueda('');
    setDpto('');
    setEstado('');
    setSolicitante('');
    setPagina(1);
  };

  const filtrados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return tickets.filter((ticket) => (
      (solicitante === '' || ticket.usuario_creador === solicitante)
      && (!texto || ticket.asunto.toLowerCase().includes(texto) || String(ticket.identificador).toLowerCase().includes(texto))
      && (dpto === '' || ticket.dpto === dpto)
      && (estado === '' || ticket.estado_del_ticket === estado)
    ));
  }, [tickets, solicitante, busqueda, dpto, estado]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / TICKETS_POR_PAGINA));
  const paginaSegura = Math.min(pagina, totalPaginas);
  const visibles = filtrados.slice((paginaSegura - 1) * TICKETS_POR_PAGINA, paginaSegura * TICKETS_POR_PAGINA);

  // ── Pestaña de personas ───────────────────────────────────────────────
  // Primero quien acumula más tickets abiertos: es lo que reclama atención
  const personas = useMemo(() => {
    const texto = busquedaPersona.trim().toLowerCase();
    return equipo
      .filter((p) => !texto
        || `${p.nombre || ''} ${p.apellidos || ''} ${p.usuario} ${p.email || ''} ${(p.webs || []).join(' ')}`
          .toLowerCase().includes(texto))
      .sort((a, b) => b.tickets_abiertos - a.tickets_abiertos
        || `${a.nombre || ''} ${a.apellidos || ''}`.localeCompare(`${b.nombre || ''} ${b.apellidos || ''}`));
  }, [equipo, busquedaPersona]);

  const totalPaginasPersonas = Math.max(1, Math.ceil(personas.length / PERSONAS_POR_PAGINA));
  const paginaPersonasSegura = Math.min(paginaPersonas, totalPaginasPersonas);
  const personasVisibles = personas.slice(
    (paginaPersonasSegura - 1) * PERSONAS_POR_PAGINA,
    paginaPersonasSegura * PERSONAS_POR_PAGINA,
  );

  /** Salta a los tickets de una persona desde el listado. */
  const verTicketsDe = (usuario) => {
    setSolicitante(usuario);
    setPagina(1);
    setPestana('tickets');
  };

  /** Los dominios se guardan sin esquema: hay que añadirlo para que enlacen. */
  const urlDeWeb = (web) => (/^https?:\/\//i.test(web) ? web : `https://${web}`);

  const avatar = (persona) => (persona.fotoPerfil && persona.fotoPerfil !== 'none'
    ? (persona.fotoPerfil.startsWith('http') ? persona.fotoPerfil : `${IMAGE_DOMAIN}${persona.fotoPerfil}`)
    : `${IMAGE_DOMAIN}/images/default-user.png`);

  if (consulta.isPending) return <div className="loader-fullscreen"><Loader /></div>;

  // Sin equipo la sección no aporta nada: se explica en vez de dejar un vacío
  if (equipo.length === 0) {
    return (
      <main className="site-width equipo-page">
        <div className="pagina-cabecera">
          <h2>{t('equipo.titulo')}</h2>
          <p>{t('equipo.subtitulo')}</p>
        </div>
        <div className="equipo-vacio">
          <FiUsers />
          <h3>{t('equipo.sinEquipoTitulo')}</h3>
          <p>{t('equipo.sinEquipoTexto')}</p>
        </div>
      </main>
    );
  }

  const pestanas = [
    { id: 'tickets', label: t('equipo.tabTickets'), icono: FiFileText },
    { id: 'personas', label: t('equipo.tabPersonas'), icono: FiUsers },
  ];

  return (
    <main className="site-width equipo-page">
      <div className="pagina-cabecera">
        <h2>{t('equipo.titulo')}</h2>
        <p>{t('equipo.subtituloCon', { count: equipo.length, abiertos: totalAbiertos })}</p>
      </div>

      <Pestanas
        pestanas={pestanas}
        activa={pestana}
        onCambiar={setPestana}
        etiqueta={t('equipo.seccionesEquipo')}
      />

      {pestana === 'tickets' ? (
        <>
          <div className="dt-filtros-bar eq-filtros">
            <div className="dt-search">
              <FiSearch className="dt-search-icon" />
              <input
                type="text"
                className="dt-search-input"
                value={busqueda}
                onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }}
                placeholder={t('equipo.buscarTicket')}
              />
            </div>
            {/* La persona es un filtro más: con decenas de empleados, una ficha
                por cabeza no cabe en pantalla */}
            <DtDropdown
              options={equipo.map((p) => ({ value: p.usuario, label: nombreDe(p) }))}
              value={solicitante}
              onChange={(valor) => { setSolicitante(valor); setPagina(1); }}
              placeholder={t('equipo.todoElEquipo')}
            />
            <DtDropdown
              options={DEPARTAMENTOS.map((valor) => ({ value: valor, label: t(`tickets.departamentos.${valor}`) }))}
              value={dpto}
              onChange={(valor) => { setDpto(valor); setPagina(1); }}
              placeholder={t('tickets.todosDepartamentos')}
            />
            <DtDropdown
              options={ESTADOS.map((valor) => ({ value: valor, label: t(`tickets.estados.${valor}`) }))}
              value={estado}
              onChange={(valor) => { setEstado(valor); setPagina(1); }}
              placeholder={t('tickets.todosEstados')}
            />
            {hayFiltros && (
              <button type="button" className="dt-filtros-limpiar" onClick={limpiarFiltros}>
                <FiX /> {t('tickets.limpiar')}
              </button>
            )}
          </div>

          <div className="dt-table-card dt-table-card--usuario dt-table-card--equipo ticket-table-surface">
            <div className="dt-table-header dt-tabla--equipo">
              <span>{t('tickets.ticket')}</span>
              <span>{t('equipo.solicitante')}</span>
              <span>{t('tickets.estado')}</span>
              <span>{t('tickets.departamento')}</span>
              <span>{t('tickets.fecha')}</span>
              <span>{t('tickets.fechaLimite')}</span>
              <span>{t('tickets.asignadoA')}</span>
            </div>

            <div className="dt-ticket-list">
              {visibles.length === 0 ? (
                <div className="dt-empty-state">
                  <FiInbox />
                  <p>{hayFiltros ? t('tickets.sinCoincidencias') : t('equipo.sinTickets')}</p>
                  {hayFiltros && (
                    <button type="button" className="dt-empty-reset" onClick={limpiarFiltros}>{t('tickets.limpiarFiltros')}</button>
                  )}
                </div>
              ) : visibles.map((ticket) => (
                <Link
                  key={ticket.identificador}
                  className="dt-ticket-row dt-tabla--equipo"
                  to={`/ticket/${ticket.identificador}`}
                >
                  <div className="dt-col-ticket">
                    <div className="dt-ticket-icon"><FiHash /></div>
                    <div className="dt-col-ticket-texto">
                      <span className="dt-ticket-id">#{ticket.identificador}</span>
                      <span className="dt-ticket-asunto">{ticket.asunto}</span>
                    </div>
                  </div>

                  <div className="dt-col-solicitante">
                    {nombrePorUsuario[ticket.usuario_creador] || ticket.usuario_creador}
                  </div>

                  <div className="dt-col-badges">
                    <div className="dt-col-estado">
                      {ticket.estado_del_ticket && (
                        <p className={ticket.estado_del_ticket}>{t(`tickets.estados.${ticket.estado_del_ticket}`)}</p>
                      )}
                    </div>
                    <div className="dt-col-dpto">
                      {ticket.dpto && (
                        <p className={ticket.dpto.replace('_', '-')}>{t(`tickets.departamentos.${ticket.dpto}`)}</p>
                      )}
                    </div>
                  </div>

                  <div className="dt-col-fecha">{new Date(ticket.createdAt).toLocaleDateString()}</div>
                  <div className="dt-col-fecha dt-col-limite">{formatearFechaLimite(ticket.fecha_limite) || '—'}</div>

                  <div className="dt-col-asignado">
                    {ticket.usuario_encargado === 'sin_asignar' ? (
                      <>
                        <span className="dt-asignado-icono dt-asignado-icono-vacio"><FiUserX /></span>
                        <p>{t('tickets.sinAsignar')}</p>
                      </>
                    ) : (
                      <>
                        <span className="dt-asignado-icono dt-asignado-icono-activo"><FiUserCheck /></span>
                        <p>@{ticket.usuario_encargado}</p>
                      </>
                    )}
                  </div>
                </Link>
              ))}
            </div>

            {filtrados.length > TICKETS_POR_PAGINA && (
              <div className="dt-pagination">
                <button
                  type="button"
                  className="dt-pagination-btn"
                  onClick={() => setPagina((p) => Math.max(p - 1, 1))}
                  disabled={paginaSegura === 1}
                  aria-label={t('tickets.paginaAnterior')}
                >
                  <FiChevronLeft />
                </button>
                <span className="dt-pagination-info">{t('tickets.paginaDe', { actual: paginaSegura, total: totalPaginas })}</span>
                <button
                  type="button"
                  className="dt-pagination-btn"
                  onClick={() => setPagina((p) => Math.min(p + 1, totalPaginas))}
                  disabled={paginaSegura === totalPaginas}
                  aria-label={t('tickets.paginaSiguiente')}
                >
                  <FiChevronRight />
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="dt-filtros-bar eq-filtros">
            <div className="dt-search">
              <FiSearch className="dt-search-icon" />
              <input
                type="text"
                className="dt-search-input"
                value={busquedaPersona}
                onChange={(e) => { setBusquedaPersona(e.target.value); setPaginaPersonas(1); }}
                placeholder={t('equipo.buscarPersona')}
              />
            </div>
          </div>

          <div className="dt-table-card ticket-table-surface">
            <div className="dt-table-header dt-tabla--personas">
              <span>{t('equipo.persona')}</span>
              <span>{t('equipo.contacto')}</span>
              <span>{t('equipo.abiertos')}</span>
              <span>{t('equipo.totalTickets')}</span>
              <span />
            </div>

            <div className="dt-ticket-list">
              {personasVisibles.length === 0 ? (
                <div className="dt-empty-state">
                  <FiUsers />
                  <p>{t('equipo.sinPersonas')}</p>
                </div>
              ) : personasVisibles.map((persona) => (
                <div key={persona.usuario} className="dt-ticket-row dt-tabla--personas">
                  <div className="dt-col-ticket">
                    <img className="eq-avatar" src={avatar(persona)} alt="" aria-hidden="true" />
                    <div className="dt-col-ticket-texto">
                      <span className="dt-ticket-asunto">{nombreDe(persona)}</span>
                      <span className="eq-usuario">@{persona.usuario}</span>
                    </div>
                  </div>

                  <div className="eq-col-contacto">
                    <a href={`mailto:${persona.email}`} title={persona.email}><FiMail aria-hidden="true" /> {persona.email}</a>
                    {persona.telefono && (
                      <a href={`tel:${persona.telefono}`} title={persona.telefono}><FiPhone aria-hidden="true" /> {persona.telefono}</a>
                    )}
                    {(persona.webs || []).map((web) => (
                      <a
                        key={web}
                        href={urlDeWeb(web)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={web}
                      >
                        <FiGlobe aria-hidden="true" /> {web}
                      </a>
                    ))}
                  </div>

                  <div className="eq-col-cifra">
                    <span className={persona.tickets_abiertos > 0 ? 'eq-cifra eq-cifra--activa' : 'eq-cifra'}>
                      {persona.tickets_abiertos}
                    </span>
                  </div>

                  <div className="eq-col-cifra eq-col-cifra--suave">{persona.total_tickets}</div>

                  <div className="eq-col-accion">
                    <button type="button" onClick={() => verTicketsDe(persona.usuario)}>
                      {t('equipo.verTickets')} <FiArrowRight aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {personas.length > PERSONAS_POR_PAGINA && (
              <div className="dt-pagination">
                <button
                  type="button"
                  className="dt-pagination-btn"
                  onClick={() => setPaginaPersonas((p) => Math.max(p - 1, 1))}
                  disabled={paginaPersonasSegura === 1}
                  aria-label={t('tickets.paginaAnterior')}
                >
                  <FiChevronLeft />
                </button>
                <span className="dt-pagination-info">
                  {t('tickets.paginaDe', { actual: paginaPersonasSegura, total: totalPaginasPersonas })}
                </span>
                <button
                  type="button"
                  className="dt-pagination-btn"
                  onClick={() => setPaginaPersonas((p) => Math.min(p + 1, totalPaginasPersonas))}
                  disabled={paginaPersonasSegura === totalPaginasPersonas}
                  aria-label={t('tickets.paginaSiguiente')}
                >
                  <FiChevronRight />
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </main>
  );
};

export default MiEquipo;
